/**
 * Förhandlingsordning på bostadshyresmarknaden — the static clauses.
 *
 * Verbatim from the Haymanot-approved Word templates. DO NOT paraphrase or
 * reorder. Points are numbered by the assembler (the three-part variant's
 * extra party point shifts every later point by one on its own). Punkt
 * "Förhandlingsskyldighet" cites the innebörd point by number ("punkten 4
 * a–c" / "punkten 5 a–c"); that number is the `innebord_number` input
 * (allowlisted in consistency.allowlist.ts — a point-style `sectionRef`
 * would print "4." and change the legal text).
 *
 * The same clause set backs both variants.
 */
import { list, nodes, p, section, t } from '@hyranai/regta-document-ir';
import { str } from '../lib.js';
function clauseNodes(body) {
    return body.flatMap((part) => {
        if (typeof part === 'string')
            return [p(part)];
        if ('ordered' in part)
            return [list([...part.ordered], { ordered: true })];
        const { lead, items, tail } = part.inline;
        const content = [t(lead)];
        items.forEach((item, index) => {
            content.push(t(` (${index + 1}) ${item}${index < items.length - 1 ? ';' : ''}`));
        });
        return nodes(p(content), tail ? p(tail) : null);
    });
}
function foClause(spec) {
    return {
        blockKey: spec.blockKey,
        version: 'v1',
        plane: 'legal',
        status: 'published',
        language: 'sv-SE',
        title: spec.title,
        summary: spec.summary,
        legalReview: { reviewedBy: 'Haymanot', source: SOURCE },
        // `innebord_number` is a setting the composition fixes, not a form field.
        slots: [],
        compile: (input) => [
            section(spec.ref, spec.title, clauseNodes(typeof spec.body === 'function' ? spec.body(input) : spec.body), {
                keepTogether: true,
                variant: 'clause',
            }),
        ],
    };
}
const SOURCE = 'base-templates/Förhandlingsordning (Haymanot-approved)';
export const CLAUSE_4_INTRO = 'Förhandlingsordningen innebär:';
export const CLAUSE_4A_LEAD = 'a) Skyldighet för hyresvärdsparten att en god tid i förväg på eget initiativ påkalla förhandling med hyresgästparten i följande frågor:';
export const CLAUSE_4A_ITEMS = [
    'höjning av hyran för en lägenhet, om inte höjningen avser ett tillval och högst motsvarar ett pris som förhandlingsordningens parter har rekommenderat,',
    'bestämmande av hyran för en ny hyresgäst, om den begärda hyran överstiger den tidigare hyresgästens hyra och höjningen inte avser ett tillval och högst motsvarar ett pris som förhandlingsordningens parter har rekommenderat,',
    'ändring av grunderna för beräkning av sådan särskild ersättning som avses i 12 kap. 19 § jordabalken, eller',
    'ändring av villkor som avser hyresgästernas rätt att använda utrymmen som är avsedda att användas gemensamt av hyresgästerna.',
];
export const CLAUSE_4B = 'b) Skyldighet för part att, under förutsättning att parterna inte kommit överens om annat, påkalla förhandling med motparten i fråga om hyresvillkoren, lägenheternas och husets skick, gemensamma anordningar i huset, övriga boendeförhållanden, i den mån de rör hyresgästerna gemensamt.';
export const CLAUSE_4C = 'c) Rätt för hyresgästparten att, under förutsättning att hyresgästen genom skriftlig fullmakt har gett hyresgästparten rätt att företräda honom eller henne i saken, påkalla förhandling med hyresvärdsparten om fråga som rör enskild hyresgästs boendeförhållande eller tillämpning av enskilt hyresavtal.';
export const CLAUSE_7_LEAD = 'Begäran om förhandling ska göras skriftligen och innehålla följande:';
export const CLAUSE_7_ITEMS = [
    'uppgift om den eller de frågor som det begärs förhandling om',
    'uppgift om vilka fastigheter och lägenheter som berörs av förhandlingen',
    'förslag på tidpunkter och plats för sammanträde om den inte ska hållas digitalt.',
];
export const CLAUSE_7_TAIL = 'Framställning får göras digitalt enligt de instruktioner som följer av en särskild överenskommelse om digitaliserat förhandlingsförfarande.';
export const innebord = foClause({
    blockKey: 'fo.innebord',
    ref: 'fo-innebord',
    title: 'Förhandlingsordningens innebörd',
    summary: 'What the negotiating order obliges each party to do (4 a–c) — cited by later clauses.',
    body: [
        CLAUSE_4_INTRO,
        CLAUSE_4A_LEAD,
        { ordered: CLAUSE_4A_ITEMS },
        CLAUSE_4B,
        CLAUSE_4C,
    ],
});
export const forhandlingsskyldighet = foClause({
    blockKey: 'fo.forhandlingsskyldighet',
    ref: 'fo-forhandlingsskyldighet',
    title: 'Förhandlingsskyldighet',
    summary: 'The duty to negotiate once a matter under the innebörd point (a–c) is raised, plus the duty of loyalty.',
    body: (input) => [
        `Hyresvärdsparten och Hyresgästparten ("Parterna") har en förhandlingsskyldighet om förhandling har påkallats enligt punkten ${str(input.innebord_number) ?? '4'} a–c. Hyresgästsidan får endast lämna ut överenskommelser som ingåtts mellan Parterna till berörda hyresgäster eller när part är skyldig att lämna ut sådan överenskommelse till myndighet eller domstol.`,
        'Parterna ska iaktta lojalitet och i övrigt genom ömsesidigt hänsynstagande till förhandlingsordningens långvarighet, söka att i samförstånd ordna sina gemensamma angelägenheter och såvitt möjligt förebygga uppkomsten av tvister.',
    ],
});
export const foreningsratten = foClause({
    blockKey: 'fo.foreningsratten',
    ref: 'fo-foreningsratten',
    title: 'Föreningsrätten',
    summary: 'The parties shall leave the right of association unimpaired.',
    body: ['Parterna ska lämna föreningsrätten okränkt.'],
});
export const framstallning = foClause({
    blockKey: 'fo.framstallning',
    ref: 'fo-framstallning',
    title: 'Förhandlingsframställning',
    summary: 'What a written request for negotiation must contain, and that it may be made digitally.',
    body: [
        { inline: { lead: CLAUSE_7_LEAD, items: CLAUSE_7_ITEMS, tail: CLAUSE_7_TAIL } },
    ],
});
export const sammantradeProtokoll = foClause({
    blockKey: 'fo.sammantrade-protokoll',
    ref: 'fo-sammantrade',
    title: 'Sammanträde och protokoll',
    summary: 'Meeting within three weeks, negotiation conducted promptly, minutes on request.',
    body: [
        'Parterna ska boka ett förhandlingssammanträde inom tre veckor efter att motparten fått del av förhandlingsframställningen om det inte rör sig om förhandling avseende en fråga där Parterna skriftligen kommit överens om annat. Förhandling ska bedrivas skyndsamt och lojalt. Respektive förhandlingspart ska framlägga motiverat förslag till lösning av de frågor som förhandlingen avser.',
        'Vid förhandling ska, om någon part begär det, föras protokoll som justeras av Parterna.',
    ],
});
export const overenskommelse = foClause({
    blockKey: 'fo.overenskommelse',
    ref: 'fo-overenskommelse',
    title: 'Förhandlingsöverenskommelse',
    summary: 'Agreements must be written and signed; electronic signature is permitted under conditions.',
    body: [
        'Förhandlingsöverenskommelser ska upprättas skriftligen och undertecknas av förhandlingsordningens parter. Parterna får underteckna förhandlingsöverenskommelser genom elektronisk signering i enlighet med tillämplig lagstiftning, under förutsättning att signeringen möjliggör säker identifiering av undertecknaren och säkerställer handlingens integritet.',
    ],
});
export const tvistlosning = foClause({
    blockKey: 'fo.tvistlosning',
    ref: 'fo-tvistlosning',
    title: 'Tvistlösning genom särskild skiljeman',
    summary: 'Recourse to a special arbitrator under 23 a § HFL, and how costs are shared.',
    body: [
        'Om Parterna inte kommer överens, får respektive part hos relevant myndighet ansöka om utseendet av en särskild skiljeman för att denne ska pröva en tvist som omfattas av 23 a § hyresförhandlingslagen. Parterna ska omsätta den särskilda skiljemannens rekommendation i en förhandlingsöverenskommelse enligt hyresförhandlingslagen. Detta ska ske inom 14 dagar från det att rekommendationen lämnades.',
        'Parterna står för sina egna kostnader och ersättningen till skiljemannen ska fördelas lika mellan Parterna.',
    ],
});
export const strandning = foClause({
    blockKey: 'fo.strandning',
    ref: 'fo-strandning',
    title: 'Strandning',
    summary: 'How a negotiation is ended when no agreement can be reached (18 § / 23 a § HFL).',
    body: [
        'Kan överenskommelse inte träffas ska förhandlingen avslutas genom att endera parten skriftligen meddelar att man frånträder förhandlingarna. För sådan tvist som anges i 23 a § hyresförhandlingslagen gäller i stället vad som är särskilt stadgat i 18 och 23 a §§ hyresförhandlingslagen.',
    ],
});
export const hyressattningsavgift = foClause({
    blockKey: 'fo.hyressattningsavgift',
    ref: 'fo-hyressattningsavgift',
    title: 'Hyressättningsavgift',
    summary: 'A negotiation fee may be included in the rent, disclosed and capped at what is reasonable.',
    body: [
        'Genom en förhandlingsöverenskommelse får bestämmas att i hyran för samtliga lägenheter som omfattas av förhandlingsordningen ska ingå ett visst belopp som utgör ersättning till hyresgästorganisationen för dess förhandlingsarbete. Av förhandlingsöverenskommelsen ska det framgå hur stor del av totalhyran som utgör ersättning av detta slag. Beloppet får inte överstiga vad som kan anses skäligt med hänsyn till hyran i övrigt, förhandlingskostnadernas storlek och övriga omständigheter.',
    ],
});
export const nyForhandlingsordning = foClause({
    blockKey: 'fo.ny-forhandlingsordning',
    ref: 'fo-ny-forhandlingsordning',
    title: 'Ny förhandlingsordning',
    summary: 'This agreement replaces any earlier negotiating order for the house.',
    body: [
        'Detta avtal om förhandlingsordning ersätter, i förekommande fall, tidigare förhandlingsordning som gällt för huset fram tills detta avtals ikraftträdande.',
    ],
});
/** The clause spine, in document order. Identical for both variants. */
export const FO_STATIC_CLAUSES = [
    innebord,
    forhandlingsskyldighet,
    foreningsratten,
    framstallning,
    sammantradeProtokoll,
    overenskommelse,
    tvistlosning,
    strandning,
    hyressattningsavgift,
    nyForhandlingsordning,
];
//# sourceMappingURL=clauses.js.map